import { useCallback, useEffect, useRef } from "react";
import { useFaust } from "../../contexts/FaustContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { ExternalLink, X, Info } from "lucide-react";
import { cn } from "../../lib/utils";

export function BottomPanel() {
    const { state, dispatch, stopDsp } = useFaust();
    const diagramRef = useRef<HTMLDivElement>(null);
    const plotCanvasRef = useRef<HTMLDivElement>(null);

    // Inject diagram SVG when it changes
    useEffect(() => {
        if (!diagramRef.current || !state.diagramSvg) return;
        diagramRef.current.innerHTML = state.diagramSvg;
        const svg = diagramRef.current.querySelector("svg");
        if (svg) {
            svg.style.width = "100%";
            svg.style.height = "100%";
            svg.style.maxWidth = "500%";
            svg.style.minWidth = "20%";
            svg.style.cursor = "grab";
        }
    }, [state.diagramSvg]);

    // SVG pan/zoom
    useEffect(() => {
        const container = diagramRef.current;
        if (!container) return;

        const handleWheel = (e: WheelEvent) => {
            if (!e.ctrlKey) return;
            const svg = container.querySelector("svg");
            if (!svg) return;
            e.preventDefault();
            const d = e.deltaY > 0 ? 1 : -1;
            const w = svg.clientWidth;
            svg.style.width = `${w * (1 - d * 0.25)}px`;
        };

        let isDragging = false;
        let startX = 0;
        let startY = 0;
        let scrollL = 0;
        let scrollT = 0;

        const handleMouseDown = (e: MouseEvent) => {
            if (e.target instanceof SVGElement || (e.target as Element)?.closest?.("svg")) {
                isDragging = true;
                startX = e.pageX;
                startY = e.pageY;
                scrollL = container.parentElement?.scrollLeft || 0;
                scrollT = container.parentElement?.scrollTop || 0;
                e.preventDefault();
            }
        };

        const handleMouseMove = (e: MouseEvent) => {
            if (!isDragging) return;
            const dx = e.pageX - startX;
            const dy = e.pageY - startY;
            if (container.parentElement) {
                container.parentElement.scrollLeft = scrollL - dx;
                container.parentElement.scrollTop = scrollT - dy;
            }
        };

        const handleMouseUp = () => {
            isDragging = false;
        };

        container.addEventListener("wheel", handleWheel, { passive: false });
        container.addEventListener("mousedown", handleMouseDown);
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);

        return () => {
            container.removeEventListener("wheel", handleWheel);
            container.removeEventListener("mousedown", handleMouseDown);
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, []);

    const handlePopup = useCallback(() => {
        window.open(
            "faust-ui/index.html",
            "Faust DSP",
            "directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no,width=800,height=600"
        );
    }, []);

    const handleStopDsp = useCallback(() => {
        stopDsp();
        dispatch({ type: "SET_ACTIVE_BOTTOM_TAB", tab: "diagram" });
    }, [stopDsp, dispatch]);

    return (
        <Tabs
            value={state.activeBottomTab}
            onValueChange={(tab) => dispatch({ type: "SET_ACTIVE_BOTTOM_TAB", tab })}
            className="h-full flex flex-col"
        >
            <TabsList className="h-8 bg-black border-b border-white/[0.08] rounded-none justify-start px-1 gap-0 shrink-0">
                <TabsTrigger
                    value="diagram"
                    className="h-7 rounded-none rounded-t border-b-2 border-transparent data-[state=active]:border-[#0078d4] data-[state=active]:bg-transparent data-[state=active]:text-zinc-100 text-zinc-500 text-xs px-3"
                >
                    Diagram
                </TabsTrigger>
                <TabsTrigger
                    value="plot"
                    className="h-7 rounded-none rounded-t border-b-2 border-transparent data-[state=active]:border-[#0078d4] data-[state=active]:bg-transparent data-[state=active]:text-zinc-100 text-zinc-500 text-xs px-3"
                >
                    Plot
                </TabsTrigger>
                {state.hasDsp && (
                    <TabsTrigger
                        value="dsp"
                        className="h-7 rounded-none rounded-t border-b-2 border-transparent data-[state=active]:border-[#0078d4] data-[state=active]:bg-transparent data-[state=active]:text-zinc-100 text-zinc-500 text-xs px-3 gap-1.5"
                    >
                        DSP
                    </TabsTrigger>
                )}
                {state.hasDsp && (
                    <div className="flex items-center gap-0.5 ml-[-4px]">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span
                                    role="button"
                                    tabIndex={0}
                                    className="inline-flex items-center justify-center h-5 w-5 rounded-sm cursor-pointer text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800"
                                    onClick={handlePopup}
                                >
                                    <ExternalLink className="h-3 w-3" />
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>Pop out DSP UI</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span
                                    role="button"
                                    tabIndex={0}
                                    className="inline-flex items-center justify-center h-5 w-5 rounded-sm cursor-pointer text-zinc-500 hover:text-red-400 hover:bg-zinc-800"
                                    onClick={handleStopDsp}
                                >
                                    <X className="h-3 w-3" />
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>Stop DSP</TooltipContent>
                        </Tooltip>
                    </div>
                )}

                {/* Spacer */}
                <div className="flex-1" />
            </TabsList>

            {/* Diagram Tab */}
            <TabsContent value="diagram" className="flex-1 m-0 relative overflow-auto">
                {state.diagramSvg ? (
                    <div
                        ref={diagramRef}
                        className="w-full h-full flex items-center justify-center p-4"
                    />
                ) : (
                    <div className="flex items-center justify-center h-full">
                        <div className="flex flex-col items-center gap-2 text-zinc-600">
                            <Info className="h-8 w-8" />
                            <span className="text-sm">Diagram not available</span>
                            <span className="text-xs text-zinc-700">
                                Click Run to compile your Faust code
                            </span>
                        </div>
                    </div>
                )}
            </TabsContent>

            {/* Plot Tab */}
            <TabsContent value="plot" className="flex-1 m-0 relative">
                <div
                    ref={plotCanvasRef}
                    className="w-full h-full bg-black"
                    id="plot-ui-container"
                />
                {!state.hasDsp && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="flex flex-col items-center gap-2 text-zinc-600">
                            <Info className="h-8 w-8" />
                            <span className="text-sm">No data</span>
                        </div>
                    </div>
                )}
            </TabsContent>

            {/* DSP UI Tab - iframe stays mounted so it doesn't lose state */}
            <TabsContent value="dsp" className="flex-1 m-0 relative" forceMount style={{ display: state.activeBottomTab === "dsp" ? undefined : "none" }}>
                <iframe
                    id="iframe-faust-ui"
                    src="./faust-ui/index.html"
                    className={cn(
                        "w-full h-full border-none absolute inset-0",
                        state.hasDsp ? "visible" : "invisible"
                    )}
                />
                {!state.hasDsp && (
                    <div className="flex items-center justify-center h-full">
                        <div className="flex flex-col items-center gap-2 text-zinc-600">
                            <Info className="h-8 w-8" />
                            <span className="text-sm">DSP not running</span>
                            <span className="text-xs text-zinc-700">
                                Click Run to compile your Faust code
                            </span>
                        </div>
                    </div>
                )}
            </TabsContent>
        </Tabs>
    );
}
